<script setup>
import { inject, provide, ref, watch, computed, nextTick, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { postVote, getVoteResult } from '@/api/article.js'
import usePhotoswipe from '@/views/Article/Detail/components/ArticleContent/usePhotoswipe.js'
import { sha1 } from 'js-sha1'
import ThemeColor from '@/utils/themeColor'
import { appendUtmParams } from '@/utils'
import RichText from '../RichText/index.vue'
import AnchorTag from '../AnchorTag/index.vue'
import SummaryGraph from '../SummaryGraph/index.vue'
import VoteButton from '../Button/index.vue'
import VoteList from '../List/index.vue'
import SlidePane from '../List/SlidePane.vue'
import { contentCopyHandler } from '@/utils/copyright'
import { useAppStore } from '@/stores/app'

const props = defineProps({
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['viewerShown', 'viewerChange'])

const { config } = useAppStore()
const route = useRoute()
const size = inject('size')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const width = inject('width')
const mSize = computed(() => {
  const sizeMap = {
    sm: [767, 500],
    mxs: [499, -Infinity]
  }
  for (const key in sizeMap) {
    const [max, min] = sizeMap[key]
    if (max >= width.value && width.value >= min) {
      return key
    }
  }
  return size.value
})
const info = inject('activityInfo')
const extraInfo = inject('extraInfo')
// const refreshData = inject('refreshData')
const loginRef = inject('loginRef')
const sendPV = inject('sendPV')
// 深度平铺数组
const deepFlatten = (arr) => [].concat(...arr.map((v) => (Array.isArray(v) ? deepFlatten(v) : v)))
const getEventID = (params = {}) => {
  // [polling id]_[voting id]_[page event name] 如：p100001_1_submitted
  let { pid, vid, event } = params
  let _vid = vid || activeId.value
  return `p${pid || info.value.id}${_vid ? '_' + _vid : ''}${event ? '_' + event : ''}`
}

const pollingInfo = computed(() => info.value?.polling_event)
const votingInfo = computed(() => extraInfo.value?.voting_data)
const votingRule = computed(() => votingInfo.value?.voting_rule || [])
const votingList = computed(() => votingInfo.value?.voting_list || [])
// 是否已投票
const isVoted = computed(() => votingInfo.value?.user_voted)
// 投票活動狀態：1-待開始，2-進行中，3-已結束，4-已停用
const status = computed(() => pollingInfo.value?.event_status || 1)
// 1: light - 黑底  2: dark - 白底
const theme = computed(() => pollingInfo.value?.content_box_style || 2)
// 单一型投票（单项投票）
const isSingleVote = computed(() => (votingList.value.length > 1 ? false : true))
// 投票開始前：1-顯示訊息和投票列表（不能投票），2-只顯示開始前訊息
const showVoteList = computed(() => pollingInfo.value?.display_not_started || 1)
// 已选
const voteSelected = ref({})
const activeSelected = computed(() => voteSelected.value[activeId.value])
// 已选结果
const selectedList = computed(() => {
  let obj = submitResList.value || voteSelected.value
  let arr = []
  for (const [k, item] of Object.entries(obj)) {
    let res = votingList.value.find((v) => v.voting_id == k)
    arr.push({
      ...res,
      resList: res.options?.filter((v) => item.includes(v.option_id))
    })
  }
  return arr
})
// 必填未选
// --已选是满足必填数量的投票才算
const getRequiredIds = () => deepFlatten(votingRule.value.filter((v) => v.length === 1))
const requiredUnselectedList = computed(() => {
  let requiredArr = getRequiredIds()
  if (requiredArr.length === 0) return []
  let res = []
  for (const id of requiredArr) {
    if (checkVote(id)) {
      let data = votingList.value.find((v) => v.voting_id == id)
      res.push(data)
    }
  }
  return res
})
// 选填未选
// --已选是满足必填数量的投票才算
const getOptionalIds = () => deepFlatten(votingRule.value.filter((v) => v.length > 1))
const optionalUnselectedList = computed(() => {
  let optionalArr = getOptionalIds()
  if (optionalArr.length === 0) return []
  // 先找有没有已选
  let selectedArr = optionalArr.filter((id) => voteSelected.value[id].length > 0)
  if (selectedArr.length === 0)
    return votingList.value.filter((v) => optionalArr.includes(v.voting_id))
  // 再判断是否满足已选数量，返回不满足的投票列表
  let unselectedArr = selectedArr.filter((id) => checkVote(id))
  return votingList.value.filter((v) => unselectedArr.includes(v.voting_id))
})

// 复杂型投票（多项投票）
// - repeat_vote 是否可重复投票：1-仅投票一次，2-可编辑（可重复投票，覆盖上一次）
const activeId = ref('')
const activeData = computed(() => votingList.value.find((v) => v.voting_id == activeId.value))
const activeIndex = computed(() => votingList.value.findIndex((v) => v.voting_id == activeId.value))
const disabledSubmit = computed(
  () =>
    isVoted.value &&
    activeData.value?.repeat_vote == 1 &&
    activeData.value?.user_voted_options?.length > 0
)
const isStart = computed(() => activeIndex.value === 0)
const isEnd = computed(() => activeIndex.value === votingList.value.length - 1)
// 投票类型：1-必选（全部投票） 2-可选（至少一个投票） 3-必选+可选
const multipleVoteType = computed(() => {
  if (isSingleVote.value) return 0
  if (votingRule.value.length === votingList.value.length) return 1
  else if (votingRule.value.length === 1) return 2
  else return 3
})
// 获取投票类型3的投票类型
const getVote3Type = (id) => {
  let optionalArr = getOptionalIds()
  if (optionalArr.includes(id)) return 'optional'
  return 'required'
}
// 切换投票
const onVoteChange = (value, replaceState = true) => {
  activeId.value = value
  if (replaceState) {
    nextTick(() => {
      let url = appendUtmParams(
        location.origin + location.pathname + (value ? `#voting=${value}` : '')
      )
      window.history.replaceState(history.state, null, url)
      if (value) sendPV({ screenLabel: url, eventID: getEventID({ vid: value }) })
    })
  }
}
const votePrev = () => {
  activeId.value = votingList.value?.[activeIndex.value - 1]?.voting_id
}
const voteNext = () => {
  activeId.value = votingList.value?.[activeIndex.value + 1]?.voting_id
}
const voteToLoop = (index) => {
  activeId.value = votingList.value?.[index]?.voting_id
}
const voteToggle = (key) => {
  if (key === 'prev') votePrev()
  if (key === 'next') voteNext()
  anchortagRef.value?.scroll2Tag?.('vote_list')
}
// 上下页按钮
// - 没登录不用校验
const handlePrevOrNext = (key) => {
  if (key === 'prev' && isStart.value) return
  if (key === 'next' && isEnd.value) return
  if (!loginRef.value?.checklogin?.()) {
    voteToggle(key)
    return
  }
  // 上一页不用校验
  if (key === 'prev') {
    voteToggle(key)
    return
  }
  let skipCheck = false
  // 投票类型为2时，当前投票未选，跳过校验
  if (multipleVoteType.value === 2 && activeSelected.value.length === 0) {
    skipCheck = true
  }
  // 投票类型为3时，当前投票为选填且未选，跳过校验
  if (
    multipleVoteType.value === 3 &&
    activeSelected.value.length === 0 &&
    getVote3Type(activeId.value) === 'optional'
  ) {
    skipCheck = true
  }

  if (!skipCheck && checkActiveVote()) return // 校验
  voteToggle(key)
}
// 跳过按钮
// 显示逻辑：投票类型3最后一个投票是必填时，有已选选填，其它未选选填投票显示按钮，最后一个选填投票不显示
const showSkip = computed(
  () =>
    multipleVoteType.value === 3 &&
    votingRule.value.at(-1).length === 1 &&
    optionalUnselectedList.value.length === 0 &&
    activeSelected.value.length === 0 &&
    getVote3Type(activeId.value) === 'optional' &&
    getOptionalIds().at(-1) != activeId.value &&
    !isVoted.value
)
const voteSkip = () => {
  let index = votingList.value.findIndex((v) => getVote3Type(v.voting_id) === 'required')
  if (index > -1) voteToLoop(index)
}
// 提交按钮显示逻辑
// 投票类型为3第一个投票时必填时，所有必填投票已选，选填投票显示按钮
const showSubmitBtn = computed(() => {
  if (isVoted.value) return true
  if (
    multipleVoteType.value === 3 &&
    votingRule.value[0].length === 1 &&
    requiredUnselectedList.value.length === 0 &&
    getVote3Type(activeId.value) === 'optional'
  ) {
    return true
  }
  return multipleVoteType.value === 2 ? true : isEnd.value
})

// 结果页
const showResult = ref(false)
const showSuccessDialog = ref(false)
const handleBack = () => {
  // showResult.value = false
  window.open(location.origin + location.pathname, '_self')
  // window.history.replaceState(history.state, null, location.origin + location.pathname)
  // sendPV({ screenLabel: location.origin + location.pathname })
}

const onCloseQueueHandler = () => {
  if (window?.RoomQ) {
    window.RoomQ.deleteTicket(() => {
      window.console.log('Deleted RoomQ Ticket...')
    })
  }
}
const onCloseQueue = () => {
  showSuccessDialog.value = false
}

watch(
  showResult,
  (value) => {
    if (value) {
      setTimeout(() => {
        let url = appendUtmParams(location.origin + location.pathname + '#page=submitted')
        window.history.replaceState(history.state, null, url)
        if (window?.RoomQ) {
          showSuccessDialog.value = true
        }
        sendPV({ screenLabel: url, eventID: getEventID({ event: 'submitted' }) })
      }, 300)
    }
  },
  { flush: 'post' }
)
const handleShowResult = () => {
  let hash = route.hash ? route.hash.split('#page=')?.[1] : ''
  if (window?.RoomQ) {
    showSuccessDialog.value = hash === 'submitted'
  }
  if (hash === 'submitted') {
    showResult.value = isVoted.value
    if (!showResult.value) {
      window.history.replaceState(
        history.state,
        null,
        appendUtmParams(location.origin + location.pathname)
      )
      sendPV({ screenLabel: location.origin + location.pathname, eventID: getEventID() })
    }
  }
}

// 活動條款及細則
const showSubmit = ref(false)
const agreeValue = ref(false)
const showTermsDialog = ref(false)
const handleCloseTermsDialog = () => {
  agreeValue.value = true
  showTermsDialog.value = false
  if (showSubmit.value) {
    handleSubmit()
  }
}

// 获取图片主色
const bgimg = computed(() =>
  pollingInfo.value?.background_image_info?.small
    ? `url('${pollingInfo.value?.background_image_info?.small}')`
    : bgcArr.value.length > 1
      ? `linear-gradient(to bottom, ${bgcArr.value[0]}, ${bgcArr.value[1]})`
      : ''
)
const himg = computed(() => pollingInfo.value?.main_image_info?.medium)
const bgcArr = computed(() => pollingInfo.value?.background_color || [])
const tBgc = ref()
const getThemeColor = () => {
  try {
    new ThemeColor(
      himg.value ? pollingInfo.value?.main_image_info?.small : '',
      (shrinkUrl, color) => {
        tBgc.value = color
      }
    )
  } catch (error) {
    //
  }
}

// 图片查看处理
const optionImgs = computed(() => {
  let arr = []
  for (const item of votingList.value) {
    let res = item.options.filter((v) => v.option_image_info)
    if (res.length > 0) {
      let res2 = item.options.map((v) => {
        return {
          src: v.option_image_info.medium,
          thumbnail: v.option_image_info.small,
          title: v.name_tc?.trim(),
          subtitle: v.name_en?.trim(),
          desc: v.description_tc?.trim(),
          desc2: v.description_en?.trim(),
          voting: item.voting_id
        }
      })
      arr.push(...res2)
    }
  }
  return arr
})
const voteImgs = computed(() => [
  {
    src: pollingInfo.value?.main_image_info?.medium,
    thumbnail: pollingInfo.value?.main_image_info?.small
  },
  ...optionImgs.value
])
const changeFn = (pswp) => {
  emit('viewerChange', pswp)
  const vid = pswp.currSlide.data.voting
  let url = appendUtmParams(
    location.origin +
      location.pathname +
      `?photo_id=${sha1(pswp.currSlide.data.src)}` +
      (vid ? `#voting=${vid}` : '')
  )
  window.history.replaceState(history.state, null, url)
  sendPV({
    screenLabel: url,
    eventID: getEventID({ vid }),
    photo_id: sha1(pswp.currSlide.data.src)
  })
}
const { lightbox, initPS } = usePhotoswipe(voteImgs, changeFn, {
  mode: 'vote',
  isInit: false,
  mainClass: 'tvb-pswp-vote'
})
const findImgIndex = (src) => {
  return voteImgs.value.findIndex((v) => v.src === src)
}
const showViewer = (src, num = 0) => {
  const findIndex = src ? findImgIndex(src) : num
  const index = findIndex > -1 ? findIndex : 0
  lightbox.value?.loadAndOpen(index)
}

const handleInit = () => {
  // 已投票
  let obj = {}
  for (const item of votingList.value) {
    obj[item.voting_id] = item.user_voted_options || []
  }
  voteSelected.value = obj
  // 已投票默认选中
  agreeValue.value = isVoted.value
}

watch(
  () => props.loading,
  () => {
    if (!props.loading) {
      if (bgcArr.value.length === 0) getThemeColor()
      // 投票定位
      let hash = route.hash ? route.hash.split('#voting=')?.[1] : ''
      let res = votingList.value.find((v) => v.voting_id == hash)
      let hasHash = route.hash.includes('#voting=')
      onVoteChange(res?.voting_id || votingList.value?.[0]?.voting_id, hasHash)
      // 手动初始化
      handleInit()
      nextTick(() => {
        handleShowResult()
        if (!lightbox.value) {
          initPS()
          lightbox.value.on('initialLayout', () => {
            emit('viewerShown', lightbox.value.pswp)
          })
          lightbox.value.on('close', () => {
            window.history.replaceState(
              history.state,
              null,
              appendUtmParams(location.origin + location.pathname) // + location.hash
            )
            sendPV({ screenLabel: location.origin + location.pathname, eventID: getEventID() })
          })
          // 直接打开图片查看器
          setTimeout(() => {
            let photoId = route.query?.photo_id
              ? route.query?.photo_id
              : route.hash && route.hash.split('#photo_id=')?.[1]
            if (photoId) {
              const hashImgs = voteImgs.value.map((v) => sha1(v.src))
              const findIndex = hashImgs.findIndex((v) => v === photoId)
              if (findIndex > -1) {
                showViewer(null, findIndex)
              }
            }
          }, 300)
        }
      })
    }
  }
)

// 锚点跳转
const anchortagRef = ref()
watch(
  anchortagRef,
  () => {
    let hash = route.hash ? route.hash.split('#page=')?.[1] : ''
    if (anchortagRef.value?.toAnchor && hash) {
      let id = hash === 'details' ? 'rule' : hash
      setTimeout(() => anchortagRef.value.toAnchor('vote_' + id, hash, true), 800)
    }
  },
  { flush: 'post' }
)

// 校验
const checkVote = (id) => {
  let data = votingList.value.find((v) => v.voting_id == id)
  if (voteSelected.value[id].length < data?.min_option) {
    return true
  }
  return false
}
// 当前投票
const checkActiveVote = () => {
  if (activeSelected.value.length < activeData.value?.min_option) {
    warnType.value = 'warn'
    showWarnDialog.value = true
    return true
  }
  return false
}
// 是否有未选投票
const unselectedType = computed(() => {
  if (requiredUnselectedList.value.length > 0) return 'required'
  if (optionalUnselectedList.value.length > 0) return 'optional'
  return ''
})
const unselectedList = computed(() =>
  unselectedType.value === 'optional' ? optionalUnselectedList.value : requiredUnselectedList.value
)
const checkUnselected = () => {
  if (requiredUnselectedList.value.length > 0 || optionalUnselectedList.value.length > 0) {
    warnType.value = 'unselected'
    showWarnDialog.value = true
    return true
  }
  return false
}
// 前往未选
const toUnselected = (id) => {
  onVoteChange(id)
  showWarnDialog.value = false
  anchortagRef.value?.scroll2Tag?.('vote_list')
}
// 监听选择失败
const onSelectError = () => {
  errorMessage.value = '當前投票不能重複遞交'
  showWarnDialog.value = true
}

// 提交
const showWarnDialog = ref(false)
const warnType = ref('error') // error / warn / unselected
const errorMessage = ref('系統繁忙，請稍會再試')
const submitLoading = ref(false)
const submitResList = ref()
// const postSubmit = async (params) => {
//   try {
//     const res = await postVote(params)
//     if (res.code === 150001) {
//       loginRef.value?.login && loginRef.value.login()
//       submitLoading.value = false
//       return
//     }
//     if (res.code !== 0) {
//       warnType.value = 'error'
//       errorMessage.value = res?.message || '系統繁忙，請稍會再試'
//       setTimeout(() => {
//         showWarnDialog.value = true
//         submitLoading.value = false
//       }, 500)
//       return
//     }
//     submitResList.value = params?.vote_data
//     setTimeout(() => {
//       showResult.value = true
//       submitLoading.value = false
//       window.scroll({ behavior: 'smooth', top: 0, left: 0 })
//       refreshData(handleInit)
//     }, 1000)
//   } catch (error) {
//     console.warn(error)
//     warnType.value = 'error'
//     errorMessage.value = '系統繁忙，請稍會再試'
//     setTimeout(() => {
//       showWarnDialog.value = true
//       submitLoading.value = false
//     }, 500)
//   }
// }
let timeout = null
let num = 1
const getResult = async (params) => {
  try {
    const res = await getVoteResult(params)
    if (res.code === 203042 && num < 20) {
      num++
      timeout = setTimeout(() => {
        getResult(params)
      }, 1500)
      return
    }
    if (res.code !== 0) {
      warnType.value = 'error'
      errorMessage.value = res?.message || '系統繁忙，請稍會再試'
      setTimeout(() => {
        showWarnDialog.value = true
        submitLoading.value = false
        num = 1
      }, 500)
      return
    }
    submitResList.value = params?.vote_data
    setTimeout(() => {
      showResult.value = true
      submitLoading.value = false
      num = 1
      window.scroll({ behavior: 'smooth', top: 0, left: 0 })
      // refreshData(handleInit)
    }, 1000)
  } catch (error) {
    console.warn(error)
    warnType.value = 'error'
    errorMessage.value = '系統繁忙，請稍會再試'
    setTimeout(() => {
      showWarnDialog.value = true
      submitLoading.value = false
      num = 1
    }, 500)
  }
}

const postSubmit = async (params) => {
  try {
    const res = await postVote(params)
    if (res.code === 150001) {
      loginRef.value?.login && loginRef.value.login()
      submitLoading.value = false
      return
    }
    if (res.code !== 0) {
      warnType.value = 'error'
      errorMessage.value = res?.message || '系統繁忙，請稍會再試'
      setTimeout(() => {
        showWarnDialog.value = true
        submitLoading.value = false
      }, 500)
      return
    }
    timeout = setTimeout(() => getResult({ unique_id: res?.data?.unique_id }), 2000)
  } catch (error) {
    console.warn(error)
    warnType.value = 'error'
    errorMessage.value = '系統繁忙，請稍會再試'
    setTimeout(() => {
      showWarnDialog.value = true
      submitLoading.value = false
    }, 500)
  }
}

const handleSubmit = () => {
  if (loginRef.value?.checklogin?.()) {
    // 判断是否要校验
    if (!isSingleVote.value && getVote3Type(activeId.value) === 'optional') {
      // 当前投票已选校验数量
      if (activeSelected.value.length > 0) {
        if (checkActiveVote()) return
      }
    } else if (checkActiveVote()) {
      return
    }
    // if (!isVoted.value) {
    if (checkUnselected()) return
    // }

    if (!agreeValue.value && pollingInfo.value?.description_en) {
      showSubmit.value = true
      showTermsDialog.value = true
    } else {
      const params = {
        id: info.value.id, // 文章ID
        event_id: votingInfo.value?.event_id, // 活动id
        // 投票id => [选项id]
        // 如果已提交，只提交当前投票选项
        // vote_data: isVoted.value ? { [activeId.value]: activeSelected.value } : voteSelected.value
        vote_data: voteSelected.value // tc-1290 如果已提交，改为提交全部
      }
      // console.log('🚀 ~ handleSubmit ~ params:', params)
      submitLoading.value = true
      postSubmit(params)
    }
  } else {
    loginRef.value?.login?.()
  }
}

const showMemberzoneHanlder = () => {
  if (loginRef.value?.checklogin?.()) {
    loginRef.value?.memberzone?.()
    return
  }
  loginRef.value?.login?.()
}

// 交互
const listTabRef = ref()
const trendTabRef = ref()
const getTabScrollEL = (key) => document.querySelector(`.vote_${key}-multiple .v-x-scroll`)
const tabsPrev = (key) => {
  const el = getTabScrollEL(key)
  if (el) {
    el.scrollBy({
      left: el.offsetWidth * -1,
      behavior: 'smooth'
    })
  }
}
const tabsNext = (key) => {
  const el = getTabScrollEL(key)
  if (el) {
    el.scrollBy({
      left: el.offsetWidth,
      behavior: 'smooth'
    })
  }
}
watch(activeId, (value) => {
  if (value === votingList.value?.[0]?.voting_id) {
    const el = getTabScrollEL('list')
    if (el) el.scrollLeft = 0
    const el2 = getTabScrollEL('trend')
    if (el2) el2.scrollLeft = 0
  }
})
const onSwiperChange = () => {
  anchortagRef.value?.scroll2Tag?.('vote_list', 'instant')
}

const onCopyHandler = function (event) {
  contentCopyHandler(config.article_copyright_text, `${location.origin}/p${info.value.id}`, event)
}

onBeforeUnmount(() => {
  clearTimeout(timeout)
})

provide('isSingleVote', isSingleVote)
provide('voteStatus', status)
provide('showViewer', showViewer)
provide('getEventID', getEventID)
</script>

<template>
  <div
    class="tvb-vote"
    :class="[
      { 'vote-light': theme == 1, 'vote-dark': theme == 2, 'single-vote': isSingleVote },
      size ? 'vote-' + size : '',
      mSize && mSize !== size ? 'vote-' + mSize : '',
      status ? 'vote-status' + status : ''
    ]"
    @copy="onCopyHandler"
  >
    <div class="tvb-vote-header">
      <img v-lazy="himg" :alt="pollingInfo?.main_image_alt_text" @click="showViewer" />
    </div>

    <div
      class="tvb-vote-main"
      :style="{
        '--tvb-vote-bgimg-src': bgimg,
        'background-color': bgcArr.length > 0 ? bgcArr[0] : tBgc,
        'visibility: hidden': loading
      }"
    >
      <section v-if="showResult" class="vote-module vote_result">
        <div class="vote-module-header">
          <span class="vote-module-header_tit"
            ><tvb-svg-icon class="vote-result-icon" name="icon_poll_selected" />{{
              isMobile ? '' : '遞交成功！'
            }}您已投選下列選項</span
          >
          <vote-button class="back-list-btn" @click="handleBack">返回列表</vote-button>
        </div>
        <div class="vote-module-body">
          <template v-for="(item, i) in selectedList" :key="i">
            <section v-if="item.resList.length > 0" class="vote_result-list">
              <div v-if="!isSingleVote" class="vote_result-list-tit">
                {{ item?.name_tc }}
              </div>
              <slide-pane mode="result" :data="item" :list="item.resList"></slide-pane>
            </section>
          </template>
        </div>
      </section>
      <template v-else>
        <section v-if="pollingInfo?.text" class="vote-module module-space">
          <rich-text class="vote_activity-desc" :html="pollingInfo?.text"></rich-text>
        </section>

        <template v-if="!loading && (status == 1 || status == 2)">
          <section
            v-if="showVoteList === 1"
            id="vote_list"
            class="vote-module module-space vote_list"
          >
            <div
              class="vote-module-header"
              :style="
                !isSingleVote && mSize === 'mxs'
                  ? votingInfo?.voting_trend_show || pollingInfo?.description_tc
                    ? 'padding-bottom: 0;'
                    : 'padding-bottom: 0;padding-top: 0;'
                  : ''
              "
            >
              <span v-if="isSingleVote" class="vote-module-header_tit">{{
                activeData?.name_tc || '投票列表'
              }}</span>
              <span
                v-else
                v-show="status == 2 ? mSize !== 'mxs' : true"
                class="vote-module-header_tit"
                >投票列表</span
              >
              <anchor-tag
                v-if="status == 2"
                v-show="votingInfo?.voting_trend_show || pollingInfo?.description_tc"
                ref="anchortagRef"
                active="list"
                :mSize="mSize"
                :is-summary="votingInfo?.voting_trend_show"
                :is-rule="pollingInfo?.description_tc ? true : false"
              ></anchor-tag>
            </div>
            <div class="vote-module-body">
              <template v-if="isSingleVote">
                <vote-list
                  :data="activeData"
                  v-model:selected="voteSelected[activeId]"
                  @selectError="onSelectError"
                  @beforeSwiperChange="onSwiperChange"
                ></vote-list>
              </template>
              <template v-else>
                <n-tabs
                  ref="listTabRef"
                  class="vote_list-multiple"
                  :class="!isSingleVote && mSize === 'mxs' ? '' : 'tvb-hairline--top'"
                  :value="activeId"
                  @update:value="onVoteChange"
                >
                  <n-tab-pane
                    v-for="item in votingList"
                    :key="item.voting_id"
                    :name="item.voting_id"
                    :tab="item.name_tc"
                    display-directive="show:lazy"
                  >
                    <vote-list
                      :data="item"
                      v-model:selected="voteSelected[item.voting_id]"
                      @selectError="onSelectError"
                      @beforeSwiperChange="onSwiperChange"
                    ></vote-list>
                  </n-tab-pane>
                </n-tabs>

                <teleport v-if="listTabRef" to=".vote_list-multiple .n-tabs-nav-scroll-wrapper">
                  <div class="vote-tabs-prev" @click="tabsPrev('list')">
                    <tvb-svg-icon name="icon_arrow_poll" />
                  </div>
                  <div class="vote-tabs-next" @click="tabsNext('list')">
                    <tvb-svg-icon name="icon_arrow_poll" />
                  </div>
                </teleport>
              </template>
            </div>
            <div v-if="status == 2" class="vote-module-footer">
              <div
                v-if="pollingInfo?.description_en"
                class="terms-checkbox-box"
                :class="{ 'checkbox--disabled': disabledSubmit }"
              >
                <span class="checkbox-item"
                  ><tvb-svg-icon
                    class="checkbox-icon"
                    :class="{ 'checkbox-checked-icon': agreeValue }"
                    :name="
                      agreeValue ? 'icon_poll_checkbox_selected' : 'icon_poll_checkbox_default'
                    "
                    @click="agreeValue = !agreeValue" /></span
                ><span class="checkbox-item"
                  ><span>我同意是次活動</span
                  ><span class="terms-text" @click="showTermsDialog = true">《條款及細則》</span
                  ><span>，並已於</span
                  ><span
                    class="terms-text memberzone-text"
                    :class="{ 'm-memberzone-text': isMobile }"
                    @click="showMemberzoneHanlder"
                    >會員專區</span
                  ><span>更新電話號碼</span></span
                >
              </div>
              <div class="footer-btn-box">
                <template v-if="!isSingleVote">
                  <vote-button v-if="showSkip" v-show="!isMobile" type="skip" @click="voteSkip"
                    >跳過選填</vote-button
                  >
                  <vote-button v-if="!isStart && !isEnd" type="prev_next" style="flex: none">
                    <span style="z-index: 1" @click="handlePrevOrNext('prev')">上個投票</span>
                    <i class="line"></i>
                    <span style="z-index: 1" @click="handlePrevOrNext('next')">下個投票</span>
                  </vote-button>
                  <vote-button v-if="showSkip" v-show="isMobile" type="skip" @click="voteSkip"
                    >跳過選填</vote-button
                  >
                  <vote-button v-if="isEnd" type="prev" @click="handlePrevOrNext('prev')"
                    >上個投票</vote-button
                  >
                  <vote-button v-if="isStart" type="next" @click="handlePrevOrNext('next')"
                    >下個投票</vote-button
                  >
                </template>
                <vote-button
                  v-if="showSubmitBtn"
                  :type="isSingleVote ? 'default' : 'submit'"
                  :loading="submitLoading"
                  :disabled="disabledSubmit"
                  @click="handleSubmit"
                  >{{ disabledSubmit ? '已遞交' : '遞交' }}</vote-button
                >
              </div>
            </div>
          </section>

          <section
            id="vote_rule"
            v-if="status == 2 && pollingInfo?.description_tc"
            class="vote-module module-space vote_rule"
          >
            <div class="vote-module-header">
              <span class="vote-module-header_tit">投票細則</span>
              <anchor-tag
                active="rule"
                :mSize="mSize"
                :is-summary="votingInfo?.voting_trend_show"
                is-rule
              ></anchor-tag>
            </div>
            <div class="vote-module-body">
              <rich-text :html="pollingInfo?.description_tc"></rich-text>
            </div>
          </section>

          <section
            id="vote_trend"
            v-if="status == 2"
            v-show="votingInfo?.voting_trend_show"
            class="vote-module module-space vote_trend"
          >
            <div class="vote-module-header">
              <span class="vote-module-header_tit">{{ activeData?.voting_trend_text }}</span>
              <anchor-tag
                active="trend"
                :mSize="mSize"
                is-summary
                :is-rule="pollingInfo?.description_tc ? true : false"
              ></anchor-tag>
            </div>
            <div v-if="votingInfo?.voting_trend_show" class="vote-module-body">
              <template v-if="isSingleVote">
                <summary-graph :voting-id="activeData?.voting_id"></summary-graph>
              </template>
              <template v-else>
                <n-tabs
                  ref="trendTabRef"
                  class="tvb-hairline--top vote_trend-multiple"
                  :value="activeId"
                  @update:value="onVoteChange"
                >
                  <n-tab-pane
                    v-for="item in votingList"
                    :key="item.voting_id"
                    :name="item.voting_id"
                    :tab="item.name_tc"
                    display-directive="show:lazy"
                  >
                    <summary-graph
                      :show="activeData?.voting_trend_show"
                      :voting-id="item.voting_id"
                    ></summary-graph>
                  </n-tab-pane>
                </n-tabs>

                <teleport v-if="trendTabRef" to=".vote_trend-multiple .n-tabs-nav-scroll-wrapper">
                  <div class="vote-tabs-prev" @click="tabsPrev('trend')">
                    <tvb-svg-icon name="icon_arrow_poll" />
                  </div>
                  <div class="vote-tabs-next" @click="tabsNext('trend')">
                    <tvb-svg-icon name="icon_arrow_poll" />
                  </div>
                </teleport>
              </template>
            </div>
          </section>
        </template>
      </template>
    </div>

    <n-modal
      v-model:show="showTermsDialog"
      class="tvb-vote-dialog vote-terms-dialog disable-content-copy"
      :class="{ 'tvb-vote-dialog-mobile': isMobile }"
      preset="dialog"
      transform-origin="center"
      :mask-closable="false"
      :title="showSubmit && !isMobile ? '請同意活動條款及細則' : '活動條款及細則'"
      :showIcon="false"
      :on-after-leave="() => (showSubmit = false)"
    >
      <n-scrollbar :style="`max-height: ${isMobile ? 364 : 336}px`">
        <rich-text :html="pollingInfo?.description_en"></rich-text>
      </n-scrollbar>
      <template #action>
        <div v-if="isMobile" class="tvb-vote-dialog-btn-box tvb-hairline--top">
          <template v-if="disabledSubmit">
            <button class="success-btn" @click="showTermsDialog = false">關閉</button>
          </template>
          <template v-else>
            <button class="cancel-btn tvb-hairline--right" @click="showTermsDialog = false">
              關閉
            </button>
            <button class="success-btn" @click="handleCloseTermsDialog">
              {{ showSubmit ? '同意並遞交' : '同意是次活動' }}
            </button>
          </template>
        </div>
        <button
          v-else-if="!disabledSubmit"
          class="tvb-vote-dialog-pc-btn"
          @click="handleCloseTermsDialog"
        >
          {{ showSubmit ? '同意並遞交' : '同意是次活動' }}
        </button>
      </template>
    </n-modal>

    <n-modal
      v-model:show="showWarnDialog"
      class="tvb-vote-dialog vote-warn-dialog"
      :class="{
        'tvb-vote-dialog-mobile': isMobile,
        'vote-unselected-dialog': warnType == 'unselected'
      }"
      preset="dialog"
      transform-origin="center"
      :mask-closable="false"
      :showIcon="false"
    >
      <div class="vote-warn-dialog_content">
        <template v-if="warnType == 'error'">
          <tvb-svg-icon class="warn-icon" name="icon_popup_tip" />
          <div class="vote-warn-dialog_content-tit">{{ errorMessage }}</div>
        </template>
        <template v-else>
          <div class="vote-warn-dialog_content-tit">
            {{
              warnType == 'unselected'
                ? unselectedType === 'optional'
                  ? '請最少填寫以下一個活動'
                  : '請填寫以下活動'
                : `請投選${
                    activeData?.min_option === activeData?.max_option
                      ? ''
                      : activeData?.min_option + '~'
                  }${activeData?.max_option}個心水選擇`
            }}
          </div>
          <div class="vote-warn-dialog_content-subtit">
            {{ warnType == 'unselected' ? '點擊前往填寫' : `當前已選${activeSelected.length}個` }}
          </div>
        </template>
      </div>
      <n-scrollbar v-if="warnType == 'unselected'" :style="`max-height: ${isMobile ? 394 : 396}px`">
        <div class="vote-unselected-dialog_content">
          <button
            v-for="(item, i) in unselectedList"
            :key="i"
            class="tvb-hairline--surround"
            @click="toUnselected(item.voting_id)"
          >
            {{ item.name_tc }}
          </button>
        </div>
      </n-scrollbar>
      <template v-if="warnType != 'unselected'" #action>
        <div v-if="isMobile" class="tvb-vote-dialog-btn-box tvb-hairline--top">
          <button class="success-btn" @click="showWarnDialog = false">返回列表</button>
        </div>
        <button v-else class="tvb-vote-dialog-pc-btn" @click="showWarnDialog = false">
          返回列表
        </button>
      </template>
    </n-modal>

    <n-modal
      v-model:show="showSuccessDialog"
      class="tvb-vote-dialog tvb-vote-dialog-success"
      :class="{ 'tvb-vote-dialog-mobile': isMobile, 'tvb-vote-dialog-success-mobile': isMobile }"
      preset="dialog"
      transform-origin="center"
      :mask-closable="false"
      :showIcon="false"
      title="投票成功!"
      :on-before-leave="onCloseQueueHandler"
    >
      <n-scrollbar :style="`max-height: ${isMobile ? 364 : 336}px`">
        <span
          >由於現時瀏覽tvb.com網站的人數較多，為確保服務質素，閣下有機會需要重新排隊，才能再次進入tvb.com網站，謝謝您的體諒。</span
        >
      </n-scrollbar>
      <template #action>
        <div v-if="isMobile" class="tvb-vote-dialog-btn-box tvb-hairline--top">
          <button class="success-btn" @click="onCloseQueue">關閉</button>
        </div>
        <button v-else class="tvb-vote-dialog-pc-btn" type="primary" @click="onCloseQueue">
          關閉
        </button>
      </template>
    </n-modal>
  </div>
</template>

<style lang="scss">
@use '@/assets/style/photoswipe.scss';

.tvb-vote-dialog.n-dialog {
  --n-close-margin: 12px 12px 0 0 !important;
  --n-close-icon-color-hover: #f5f6f7 !important;
  --n-close-color-pressed: #fafcfd !important;
  --n-close-color-hover: transparent !important;
  --n-close-border-radius: 50% !important;
  --n-close-size: 32px !important;
  --n-close-icon-size: 14px !important;
  --n-border-radius: 16px !important;
  --n-padding: 32px !important;
  --n-content-margin: 24px 0 !important;
  width: 480px;
  max-width: calc(100vw - (32px * 2));
  overflow: hidden;

  .n-dialog__close {
    background-color: var(--n-close-icon-color-hover);
    transition: background-color 0.15s linear;
    i {
      display: none;
    }
    &::before {
      width: var(--n-close-icon-size);
      height: var(--n-close-icon-size);
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      background-image: url(@/assets/icons/icon_message_close_normal.svg);
      background-color: transparent;
      opacity: 0.9;
    }
    &::after {
      @include hairline();
      --tvb-hairline-border-radius: 50%;
      --tvb-hairline-border-width: 1px;
      --tvb-hairline-border-color: rgba(163, 178, 188, 0.35);
    }
    &:active {
      background-color: #fafcfd !important;
      &::before {
        opacity: 1;
      }
    }
  }
  &:not(.tvb-vote-dialog-mobile) .n-dialog__close:hover {
    background-color: #fff;
    &::before {
      background-image: url(@/assets/icons/icon_message_close_hover.svg);
    }
    &::after {
      border-width: var(--tvb-hairline-border-width);
    }
  }

  .n-dialog__title {
    justify-content: center;
    padding: 0 !important;
    font-weight: 500;
    font-size: 18px;
    color: #333333;
    line-height: 24px;
    letter-spacing: 0.5px;
  }

  // 滚动条样式
  .n-scrollbar {
    --n-scrollbar-width: 4px !important;
    --n-scrollbar-color: #a3b2bc59 !important;
    --n-scrollbar-color-hover: #a3b2bc59 !important;
    --n-scrollbar-border-radius: 2px !important;
  }

  .n-dialog__action {
    justify-content: center;
    button {
      border: none;
      outline: 0;
      font-size: inherit;
      cursor: pointer;
    }
    .tvb-vote-dialog-pc-btn {
      background-color: #0062a3;
      border-radius: 6px;
      padding: 10px 16px;
      font-size: 16px;
      color: #ffffff;
      line-height: 24px;
      min-width: 148px;
      letter-spacing: 0.5px;
      transition: background-color 0.15s linear;
      &:hover {
        background-color: #1a72ac;
      }
      &:active {
        background-color: #004e82;
      }
    }
  }

  &.tvb-vote-dialog-mobile {
    --n-close-margin: 5px 5px 0 0 !important;
    --n-border-radius: 10px !important;
    --n-padding: 28px 0 0 !important;
    --n-content-margin: 12px 24px 0 !important;
    .n-scrollbar-rail__scrollbar,
    .n-dialog__close {
      display: none;
    }

    .n-dialog__title {
      font-size: 17px;
      line-height: 24px;
    }

    .n-dialog__action {
      justify-content: stretch;
      .tvb-vote-dialog-btn-box {
        width: 100%;
        display: flex;
        button {
          flex: 1;
          width: 0;
          background-color: #fff;
          font-size: 16px;
          color: #666666;
          line-height: 24px;
          padding: 16px 0;
          letter-spacing: 0.5px;
        }
        .success-btn {
          color: #0062a3;
        }
      }
    }
  }
}

// 警告
.tvb-vote-dialog.vote-warn-dialog.n-dialog {
  --n-content-margin: 0 0 24px !important;
  .vote-warn-dialog_content {
    text-align: center;
    .warn-icon {
      font-size: 40px;
      vertical-align: -0.15em;
      overflow: hidden;
      margin-bottom: 12px;
    }
    .vote-warn-dialog_content-tit {
      font-weight: 500;
      font-size: 18px;
      color: #333333;
      letter-spacing: 0.5px;
      line-height: 24px;
    }
    .vote-warn-dialog_content-subtit {
      font-weight: 400;
      font-size: 16px;
      color: #818d95;
      letter-spacing: 0.5px;
      line-height: 24px;
    }
    .vote-warn-dialog_content-tit + .vote-warn-dialog_content-subtit {
      margin-top: 8px;
    }
  }

  &.tvb-vote-dialog-mobile {
    --n-content-margin: 0 0 28px !important;
    .vote-warn-dialog_content {
      .warn-icon {
        font-size: 32px;
      }
      .vote-warn-dialog_content-tit {
        font-size: 17px;
      }
      .vote-warn-dialog_content-subtit {
        color: #333333;
      }
      .vote-warn-dialog_content-tit + .vote-warn-dialog_content-subtit {
        margin-top: 10px;
      }
    }
  }
}

// 未选列表
.tvb-vote-dialog.vote-unselected-dialog.n-dialog {
  --n-padding: 24px 0 0 0 !important;
  // 滚动条样式
  .n-scrollbar {
    margin-top: 24px;
    & > .n-scrollbar-rail.n-scrollbar-rail--vertical,
    & + .n-scrollbar-rail.n-scrollbar-rail--vertical {
      right: 10px;
      bottom: 24px;
    }
  }

  .vote-unselected-dialog_content {
    padding: 0 24px 24px 24px;
    button {
      border: none;
      outline: 0;
      font-size: inherit;
      cursor: pointer;
      display: block;
      width: 100%;
      box-sizing: border-box;
      padding: 24px;
      background-color: #f8f9fa;
      border-radius: 8px;
      --tvb-hairline-border-radius: calc(2 * 8px);
      font-size: 18px;
      color: #333333;
      letter-spacing: 0.5px;
      text-align: center;
      line-height: 24px;
      transition:
        background-color 0.15s linear,
        color 0.15s linear;
      &:active {
        background-color: #fafcfd;
      }
      & + button {
        margin-top: 16px;
      }
    }
  }
  &:not(.tvb-vote-dialog-mobile) .vote-unselected-dialog_content button:hover {
    background-color: #fff;
    color: #0062a3;
  }

  &.tvb-vote-dialog-mobile {
    --n-close-icon-color-hover: transparent !important;
    --n-close-icon-size: 18px !important;
    .n-scrollbar-rail__scrollbar {
      display: block;
    }
    .n-dialog__close {
      display: flex;
    }
    .n-scrollbar {
      margin-top: 16px;
    }
    .vote-warn-dialog_content {
      .vote-warn-dialog_content-subtit {
        font-size: 14px;
        color: #818d95;
        letter-spacing: 0.3px;
        line-height: 20px;
      }
      .vote-warn-dialog_content-tit + .vote-warn-dialog_content-subtit {
        margin-top: 2px;
      }
    }
    .vote-unselected-dialog_content {
      button {
        font-size: 16px;
        padding: 14px 12px;
        border-radius: 6px;
        --tvb-hairline-border-radius: calc(2 * 6px);
        & + button {
          margin-top: 8px;
        }
      }
    }
  }
}

// 条款
.tvb-vote-dialog.vote-terms-dialog.n-dialog {
  --n-padding: 24px !important;
  width: 680px;
  .n-scrollbar-rail__scrollbar {
    display: none;
  }

  .n-dialog__content {
    background-color: #f5f6f7;
    border-radius: 8px;
    .editor-content-view {
      padding: 16px;
      font-size: 16px;
      color: #555555;
      line-height: 28px;
      text-align: left;
      letter-spacing: 0.8px;
    }
  }

  &.tvb-vote-dialog-mobile {
    --n-padding: 24px 0px 0 !important;
    .n-dialog__content {
      background-color: transparent;
      .editor-content-view {
        padding: 0 0 24px 0;
        font-size: 15px;
        line-height: 24px;
        text-align: justify;
        letter-spacing: 0.3px;
      }
    }
  }

  .tvb-vote-dialog-success {
    .n-dialog__content {
      margin-top: 8px;
      margin-bottom: 16px;

      .n-scrollbar-content {
        color: #555555;
        font-size: 16px;
        letter-spacing: 0.5px;
        line-height: 24px;
        text-align: center;
      }
    }

    &.tvb-vote-dialog-success-mobile {
      .n-dialog__content {
        margin-top: 10px;
        margin-bottom: 28px;
      }

      .n-scrollbar-content {
        color: #333333;
        font-size: 16px;
        letter-spacing: 0.5px;
        line-height: 24px;
        text-align: center;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.tvb-vote {
  margin-bottom: 100px;

  &.vote-light {
    --tvb-vote-bgc: rgba(0, 0, 0, 0.3);
    --tvb-vote-border-color: #ffffff26;
  }
  &.vote-dark {
    --tvb-vote-bgc: rgba(255, 255, 255, 0.1);
    --tvb-vote-border-color: #ffffff33;
  }
  &.vote-status1,
  &.vote-status3,
  &.vote-status4 {
    .vote-module {
      --tvb-vote-module-h-padding: 30px !important;
    }
    .vote-module.vote_list {
      .vote-module-body {
        --tvb-vote-module-b-padding: 24px;
      }
    }
  }

  .tvb-vote-header {
    img {
      width: 100%;
      cursor: pointer;
    }
    img[lazy='error'],
    img[lazy='loading'] {
      background-color: #f8f9fa;
      aspect-ratio: 16 / 9;
      padding: 0 calc(50% - (98px / 2));
      width: 100%;
      object-fit: contain;
      pointer-events: none;
    }
  }

  .tvb-vote-main {
    --tvb-vote-module-space: 48px;
    background-image: var(--tvb-vote-bgimg-src);
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center top;
    padding: 48px 32px 80px;

    .module-space {
      & + .module-space {
        margin-top: var(--tvb-vote-module-space);
      }
    }
    .vote-module {
      --tvb-vote-module-radius: 12px;
      --tvb-vote-module-h-padding: 24px;
      --tvb-vote-module-b-padding: 24px 24px 32px;
      --tvb-vote-module-f-padding: 24px;
      box-sizing: border-box;
      border-radius: var(--tvb-vote-module-radius);
      overflow: hidden;
      @include safari-overflow-hidden();
      background-color: var(--tvb-vote-bgc);
      color: rgba(255, 255, 255, 0.9);
      position: relative;
      // 解决 backdrop-filter 闪烁问题
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        z-index: 0;
        backdrop-filter: blur(50px);
      }

      .vote-module-header {
        background-color: var(--tvb-vote-bgc);
        box-sizing: border-box;
        padding: var(--tvb-vote-module-h-padding) 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 24px;
        position: relative;

        .vote-module-header_tit {
          letter-spacing: 0.5px;
          font-weight: 500;
          font-size: 20px;
          line-height: 26px;
          word-break: break-all;
        }
        .tvb-vote-anchortag {
          flex: none;
        }
      }

      .vote-module-body {
        padding: var(--tvb-vote-module-b-padding);
        position: relative;
      }

      .vote-module-footer {
        background-color: var(--tvb-vote-bgc);
        box-sizing: border-box;
        padding: var(--tvb-vote-module-f-padding);
        position: relative;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: flex-end;
      }
    }

    // 活动详情
    .vote_activity-desc {
      padding: 24px;
      position: relative;
    }
    // 走势图
    .vote_trend {
      .vote-module-body {
        padding: 0;
        .tvb-vote-trend {
          padding: var(--tvb-vote-module-b-padding);
        }
      }
    }
    // 投票列表
    .vote_list {
      .vote-module-body {
        padding: 0;
        .tvb-vote-list {
          padding: var(--tvb-vote-module-b-padding);
        }
      }
      .vote-module-footer {
        --tvb-vote-module-f-gap: 24px;
        gap: 24px;
        .terms-checkbox-box {
          display: flex;
          font-size: 15px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 22px;
          letter-spacing: 0.4px;
          user-select: none;
          margin-right: auto;
          .checkbox-icon {
            font-size: 16px;
            opacity: 0.5;
            vertical-align: -0.15em;
            overflow: hidden;
            margin-right: 8px;
            cursor: pointer;
            transition: opacity 0.15s linear;
            &:hover {
              opacity: 1;
            }
            &.checkbox-checked-icon {
              opacity: 1;
            }
          }
          .terms-text {
            cursor: pointer;
            color: rgba(255, 255, 255, 0.8);
            text-align: left;
            transition: color 0.15s linear;
            &:hover {
              color: #fff;
            }
          }

          .checkbox-item {
            display: block;
          }
          .memberzone-text {
            padding: 0px 5px;
          }
          .m-memberzone-text {
            padding: 0px 4px;
          }

          &.checkbox--disabled {
            .checkbox-icon {
              pointer-events: none;
              &.checkbox-checked-icon {
                opacity: 0.6;
              }
            }
          }
        }

        .footer-btn-box {
          display: flex;
          gap: var(--tvb-vote-module-f-gap);
        }
      }
    }

    // 投票结果
    .vote_result {
      min-height: 700px;
      .vote-result-icon {
        font-size: 24px;
        vertical-align: -0.15em;
        overflow: hidden;
        margin-right: 10px;
      }

      .vote-module-header_tit {
        display: flex;
        align-items: center;
      }

      .vote_result-list {
        & + .vote_result-list {
          margin-top: 32px;
        }

        .vote_result-list-tit {
          font-weight: 500;
          font-size: 18px;
          color: #ffffffe6;
          letter-spacing: 0.5px;
          line-height: 24px;
          margin-bottom: 16px;
        }
      }
    }

    // 标签页
    :deep(.n-tabs) {
      --n-bar-color: rgba(255, 255, 255, 0.9) !important;
      --n-tab-text-color: rgba(255, 255, 255, 0.6) !important;
      --n-tab-text-color-active: rgba(255, 255, 255, 0.9) !important;
      --n-tab-text-color-hover: var(--n-tab-text-color-active) !important;
      --n-tab-font-size: 18px !important;
      --n-tab-gap: 24px !important;
      --n-tab-padding: 20px 0 !important;
      --tvb-hairline-border-color: rgba(255, 255, 255, 0.3);
      &.tvb-hairline--top::after {
        margin: 0 calc(var(--n-tab-gap) * 2);
      }
      .n-tabs-nav {
        background-color: var(--tvb-vote-bgc);
        line-height: 1.3333333333;
      }
      .n-tabs-bar {
        height: 3px;
        border-radius: 2px;
      }
      .n-tabs-tab {
        font-weight: 500;
        letter-spacing: 0.5px;
        user-select: none;
        transition:
          box-shadow 0.15s linear,
          color 0.15s linear,
          background-color 0.15s linear,
          border-color 0.15s linear;
      }
      .n-tab-pane {
        padding: 0;
      }

      .n-tabs-nav-scroll-wrapper {
        padding: 0 var(--n-tab-gap);
        &.n-tabs-nav-scroll-wrapper--shadow-start::before,
        &.n-tabs-nav-scroll-wrapper--shadow-end::after {
          box-shadow: none;
        }
        &.n-tabs-nav-scroll-wrapper--shadow-end .vote-tabs-next {
          display: flex;
        }
        &.n-tabs-nav-scroll-wrapper--shadow-start .vote-tabs-prev {
          display: flex;
        }
      }
    }

    // 左右箭头
    .vote-tabs-prev,
    .vote-tabs-next {
      width: 32px;
      height: 32px;
      background-color: #ffffff1a;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      font-size: 16px;
      transition: background-color 0.15s linear;
      backdrop-filter: blur(50px);
      z-index: 1;

      .svg-icon {
        opacity: 0.8;
        transition: opacity 0.15s linear;
      }

      &:hover {
        background-color: #ffffff33;
        .svg-icon {
          opacity: 1;
        }
      }
    }
    .vote-tabs-prev {
      left: 22px;
    }
    .vote-tabs-next {
      right: 22px;
      .svg-icon {
        transform: scaleX(-1);
      }
    }
  }

  &.vote-s,
  &.vote-xs {
    &.vote-status1,
    &.vote-status3,
    &.vote-status4 {
      .vote-module {
        --tvb-vote-module-h-padding: 12px !important;
      }
      .vote-module.vote_list {
        .vote-module-body {
          --tvb-vote-module-b-padding: 12px;
        }
      }
    }

    .tvb-vote-main {
      --tvb-vote-module-space: 12px;
      padding: 12px 12px 40px;
      .vote-module {
        --tvb-vote-module-radius: 10px;
        --tvb-vote-module-b-padding: 12px 12px 16px;
        --tvb-vote-module-f-padding: 16px 12px;
        .vote-module-header {
          gap: 12px;
          padding: 12px;
          .vote-module-header_tit {
            padding: 0;
            letter-spacing: 0.3px;
            font-size: 15px;
            line-height: 24px;
          }
        }
        .vote-module-footer {
          // flex-direction: column;
          // justify-content: stretch;
          // align-items: stretch;
          --tvb-vote-module-f-gap: 16px;
          gap: 16px;
        }
      }

      // 活动详情
      .vote_activity-desc {
        padding: 12px;
      }
      // 走势图
      .vote_trend {
        --tvb-vote-module-b-padding: 16px 12px;
      }
      // 投票列表
      .vote_list {
        --tvb-vote-module-b-padding: 16px 12px 20px;
        .vote-module-footer {
          .terms-checkbox-box {
            font-size: 14px;
            line-height: 20px;
            letter-spacing: 0.3px;
            .checkbox-icon {
              font-size: 14px;
              margin-right: 6px;
            }
          }
        }
      }
      // 投票结果
      .vote_result {
        --tvb-vote-module-b-padding: 12px 12px 20px;
        .vote-result-icon {
          font-size: 16px;
          margin-right: 6px;
        }
        .back-list-btn {
          border-radius: 5px;
          font-size: 13px;
          line-height: 18px;
          min-width: 80px;
          padding: 4px 0;
          letter-spacing: 0;
          flex: none;
        }
        .vote_result-list {
          & + .vote_result-list {
            margin-top: 20px;
          }
          .vote_result-list-tit {
            font-weight: 400;
            font-size: 15px;
            letter-spacing: 0.3px;
            margin-bottom: 8px;
          }
        }
      }

      // 标签页
      :deep(.n-tabs) {
        --n-tab-font-size: 15px !important;
        --n-tab-gap: 16px !important;
        --n-tab-padding: 12px 0 !important;
        &.tvb-hairline--top::after {
          margin: 0 calc(var(--n-tab-gap) * 2);
        }
        .n-tabs-nav {
          line-height: 1.6;
        }
        .n-tabs-bar {
          height: 2px;
        }
        .n-tabs-tab {
          font-weight: 400;
          letter-spacing: 0.3px;
        }
      }

      .vote-tabs-prev,
      .vote-tabs-next {
        display: none !important;
      }
    }
  }

  &.vote-mxs {
    .tvb-vote-main {
      .vote-module {
        .vote-module-header {
          flex-wrap: wrap-reverse;
        }
        .vote-module-footer {
          flex-direction: column;
          justify-content: stretch;
          align-items: stretch;
          .footer-btn-box {
            flex-direction: column;
          }
        }
      }
    }
  }
}
</style>
